import { useTranslation } from '@/hooks/use-translation';
import { useAppChrome } from '@/providers/app-chrome/app-chrome-provider';
import { type MouseEvent, type ReactNode, isValidElement } from 'react';
import { Icon, Icons } from '../icon';
import { IconButton } from '../icon-button/icon-button';
import { cn } from '../lib/utils';

type ParentProp = {
  label: ReactNode;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  ariaLabel?: string;
};

type Props = {
  /** Parent breadcrumb segment. Pass `{ label, onClick }` for the canonical
   *  "label + chevron" interactive shape — `PageHeader` renders the button,
   *  the chevron, the hover transitions, and the dead-zone-free spacing.
   *  Pass a `ReactNode` if you need a fully custom node (rare). */
  parent?: ParentProp | ReactNode;
  /** Page title. Pass a `string` for the canonical heading-13 / truncate
   *  treatment — no wrapper needed. Pass a `ReactNode` (typically a
   *  `<PageHeaderTitleSelector>` inside a `<Dropdown>`) to escape into a
   *  composed/interactive title. */
  title?: ReactNode;
  /** Leading slot inside the title row (avatar, chip, identity icon).
   *  `PageHeader` wraps it with the canonical 6-px gap to the title. Only
   *  applied when `title` is a string — passing a `ReactNode` `title` opts
   *  into the escape hatch and is rendered as-is. */
  titleLeading?: ReactNode;
  /** Trailing slot inside the title row (dropdown trigger, badge, helper).
   *  Sits right after the title with the canonical 6-px gap. Only applied
   *  when `title` is a string. For right-edge actions (close button,
   *  favorite, …) use `rightAddOn` instead. */
  titleTrailing?: ReactNode;
  /** Right-aligned actions. */
  rightAddOn?: ReactNode;
  /** Optional second row rendered below the title row, inside the same
   *  bordered header block. Use for surfaces that need a persistent
   *  control strip directly under the title (e.g. an inbox filter row). */
  belowTitle?: ReactNode;
  /** Hide the leading sidebar-toggle button. Defaults to showing it —
   *  every authenticated surface wants it. */
  hideSidebarToggle?: boolean;
  className?: string;
};

const isParentProp = (value: ParentProp | ReactNode): value is ParentProp =>
  typeof value === 'object' && value !== null && !isValidElement(value) && 'label' in value;

export const PageHeader = ({
  parent,
  title,
  titleLeading,
  titleTrailing,
  rightAddOn,
  belowTitle,
  hideSidebarToggle,
  className,
}: Props) => {
  const { t } = useTranslation('desktop_common');
  const { hideSidebar, setHideSidebar } = useAppChrome();

  return (
    <header
      data-slot="page-header"
      className={cn(
        'flex w-full flex-col border-primary-5 border-b border-solid bg-white px-2',
        className,
      )}
    >
      <div className={cn('flex w-full items-center gap-0 pt-2', belowTitle ? 'pb-0.5' : 'pb-2')}>
        {!hideSidebarToggle && (
          <div data-slot="page-header-sidebar-toggle" className="flex shrink-0 items-center">
            <IconButton
              icon={Icons.SidebarLeft}
              label={t('navigation.toggle')}
              variant="ghost"
              size="sm"
              onClick={() => setHideSidebar(!hideSidebar)}
              // No left transparent border — the toggle is always the leftmost
              // slot in `PageHeader`, so the dead-zone-free pattern only needs
              // to span its right side.
              className={cn(
                'box-content border-r-2 border-r-transparent bg-clip-padding text-primary-50',
              )}
            />
          </div>
        )}
        <div className="flex min-w-0 flex-1 items-center gap-0">
          {parent && (
            <div data-slot="page-header-parent" className="flex shrink-0 items-center">
              {isParentProp(parent) ? <Breadcrumb {...parent} /> : parent}
            </div>
          )}
          {typeof title === 'string' ? (
            <div
              data-slot="page-header-title-row"
              className="flex min-w-0 flex-1 items-center gap-1.5"
            >
              {titleLeading && (
                <div data-slot="page-header-title-leading" className="flex shrink-0 items-center">
                  {titleLeading}
                </div>
              )}
              <span
                data-slot="page-header-title"
                className="min-w-0 truncate text-heading-13 text-primary-100"
              >
                {title}
              </span>
              {titleTrailing && (
                <div data-slot="page-header-title-trailing" className="flex shrink-0 items-center">
                  {titleTrailing}
                </div>
              )}
            </div>
          ) : (
            title
          )}
        </div>
        {rightAddOn && (
          <div data-slot="page-header-right-addon" className="flex shrink-0 items-center gap-0.5">
            {rightAddOn}
          </div>
        )}
      </div>
      {belowTitle && (
        <div data-slot="page-header-below-title" className="pb-2">
          {belowTitle}
        </div>
      )}
    </header>
  );
};

const Breadcrumb = ({ label, onClick, ariaLabel }: ParentProp) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={ariaLabel}
    className={cn(
      'group/breadcrumb flex shrink-0 cursor-pointer items-center gap-1 rounded text-body-13',
      // Right-only transparent border. The sidebar toggle (left neighbor)
      // already carries a right transparent border, so we'd double-up the
      // gap on the left if we used `adjacentClickable`.
      'box-content border-r-2 border-r-transparent bg-clip-padding',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-20/50',
    )}
  >
    <span className="text-primary-60 transition-colors duration-100 ease-out group-hover/breadcrumb:text-primary-80 group-active/breadcrumb:text-primary-100">
      {label}
    </span>
    <Icon name={Icons.ChevronRight} className="text-s text-primary-60" aria-hidden="true" />
  </button>
);
